import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { formatDate, getAuthor, getCategory, type BlogPost } from "@/pages/blog/blogData";
import { categoryBadgeClass, levelBadgeClass } from "@/pages/blog/blogStyles";

export default function ArticleHeader({ post }: { post: BlogPost }) {
  const category = getCategory(post.category);
  const author = getAuthor(post.author);

  return (
    <header className="bg-background-50 pt-28 md:pt-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <nav aria-label="Trilha de navegação" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              Início
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/blog" className="transition-colors hover:text-primary-600">
              Blog
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            {category ? (
              <>
                <Link to={`/blog/${category.slug}`} className="transition-colors hover:text-primary-600">
                  {category.name}
                </Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              </>
            ) : null}
            <span className="text-foreground-400">{post.h1}</span>
          </nav>
        </Reveal>

        <div className="mx-auto mt-8 max-w-3xl">
          <Reveal delay={60}>
            <div className="flex flex-wrap items-center gap-2">
              {category ? (
                <Link
                  to={`/blog/${category.slug}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${categoryBadgeClass(
                    category.accent,
                  )}`}
                >
                  <i className={category.icon} aria-hidden="true"></i>
                  {category.name}
                </Link>
              ) : null}
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${levelBadgeClass(
                  post.level,
                )}`}
              >
                {post.level}
              </span>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-[1.15] tracking-tight text-foreground-950 sm:text-4xl">
              {post.h1}
            </h1>
          </Reveal>

          <Reveal delay={140}>
            <p className="mt-5 text-base leading-relaxed text-foreground-600 md:text-lg">{post.excerpt}</p>
          </Reveal>

          <Reveal delay={180}>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3 border-y border-background-200 py-4">
              <span className="flex items-center gap-3">
                <img
                  src={author.avatar}
                  alt={author.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
                <span className="leading-tight">
                  <span className="block text-sm font-semibold text-foreground-950">{author.name}</span>
                  <span className="block text-xs text-foreground-500">{author.role}</span>
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  Publicado em {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  Atualizado em {formatDate(post.updatedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {post.readingTime}
                </span>
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={220}>
          <div className="mx-auto mt-8 max-w-4xl overflow-hidden rounded-xl border border-background-200">
            <img
              src={post.image}
              alt={post.imageAlt}
              className="h-64 w-full object-top md:h-[380px]"
            />
          </div>
        </Reveal>
      </div>
    </header>
  );
}